/**
 * NIGHTBOUND - global balance constants.
 *
 * Every tunable number in the game lives here so that game feel can be
 * adjusted without hunting through components. The server is the only place
 * these values are *applied*; the client imports them only for display.
 */

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export const SECOND_MS = 1_000
export const MINUTE_MS = 60 * SECOND_MS
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

/** Integration step used by the offline simulation. */
export const SIMULATION_STEP_MS = 5 * MINUTE_MS

/** Upper bound for a single catch-up simulation (120 days). */
export const MAX_SIMULATION_MS = 120 * DAY_MS

// ---------------------------------------------------------------------------
// Needs
// ---------------------------------------------------------------------------

export const NEED_MIN = 0
export const NEED_MAX = 100

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min
  return value < min ? min : value > max ? max : value
}

export function clampNeed(value: number): number {
  return clamp(value, NEED_MIN, NEED_MAX)
}

export function round1(value: number): number {
  return Math.round(value * 10) / 10
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100
}

export const NEEDS = {
  /**
   * `hunger` is stored as *fullness* (satiety): 100 = fully fed, 0 = starving.
   * This matches the design brief ("Hunger decreases over time", "Hunger < 25
   * means the creature is hungry").
   */
  hungerDecayPerHour: 3.5,
  hygieneDecayPerHour: 2.2,
  /** Sanity bleeds away slowly when awake and neglected. */
  sanityDriftPerHour: 0.15,
  /** Sanity recovers while the creature sleeps. */
  sanityRecoveryPerHour: 1.1,
  /** Extra sanity pressure when a need is critical. */
  sanityPenaltyLowHunger: 0.25,
  sanityPenaltyLowHygiene: 0.2,
  /** A strong bond is quietly comforting. */
  sanityBonusHighBond: 0.1,
  /** Bond bleeds away while a need is critical... */
  bondNeglectPerHour: 0.5,
  /** ...and slowly when the player vanishes for days. */
  bondAbandonmentPerHour: 0.2,
  bondAbandonmentAfterHours: 72,
  /** Health: starving creatures (no food AND filthy) lose health very slowly. */
  starvingHpLossPerHour: 0.34,
  /** Well-kept creature heals slowly on its own. */
  naturalHpRegenPerHour: 0.4,
  naturalHpRegenMinHunger: 60,
  naturalHpRegenMinHygiene: 60,
  /** The creature never dies - it can only be at 1 HP. */
  minimumHp: 1,
} as const

export const NEED_THRESHOLDS = {
  hunger: 25,
  hygiene: 25,
  energy: 20,
  bond: 20,
  sanity: 30,
  unwellHpRatio: 0.4,
} as const

// ---------------------------------------------------------------------------
// Sleep
// ---------------------------------------------------------------------------

export const SLEEP = {
  /** Below this the creature is happy to be put to bed. */
  maxEnergyToSleep: 92,
  /** It wakes itself once this rested. */
  autoWakeEnergy: 96,
  /** ...or after this long, whichever comes first. */
  maxSleepHours: 10,
  /** Sleeping with a critical hunger does not fully rest. */
  hungrySleepPenalty: 0.6,
  hungrySleepThreshold: 15,
  /**
   * At or below this much energy an awake creature simply collapses into
   * sleep. It keeps a worn-out creature from being stuck at zero forever.
   */
  collapseEnergy: 5,
} as const

// ---------------------------------------------------------------------------
// Energy
// ---------------------------------------------------------------------------
//
// Energy is the primary limiter on repeated play and training. It trickles
// back on its own while the creature is awake, and returns quickly while it
// sleeps - faster still during its natural sleep window. Training and play
// spend far more than passive recovery returns, so resting is what refills it.

export const ENERGY = {
  /** Awake: +1 energy every 5 minutes (12 per hour). */
  awakeRegenPerHour: 12,
  /** Asleep: +1 energy every minute (60 per hour). */
  sleepRegenPerHour: 60,
  /**
   * Sleeping inside the species' preferred window adds 25% to *both* energy
   * and sanity recovery.
   */
  preferredWindowMultiplier: 1.25,
} as const

// ---------------------------------------------------------------------------
// Actions (server-validated)
// ---------------------------------------------------------------------------

export const COOLDOWNS_SECONDS = {
  FEED: 20,
  CLEAN: 20,
  PLAY: 15,
  TRAIN: 18,
  PET: 8,
  SLEEP: 0,
  WAKE: 0,
} as const

export type CooldownAction = keyof typeof COOLDOWNS_SECONDS

export const ACTION_COSTS = {
  PLAY: { energy: 12, hunger: 2, hygiene: 2 },
  TRAIN: { energy: 15, hunger: 6, hygiene: 4 },
  CLEAN_TRUST_COST: 0,
} as const

export const ACTION_GAINS = {
  PET: { bond: 0.4, sanity: 0.15 },
  PLAY: { bond: 3, fitness: 1.2, sanity: 0.8 },
  CLEAN: { bond: 1.5, hygiene: 55, sanity: 1.2 },
  TRAIN: { bond: 1, fitness: 1.5 },
  FEED: { bond: 1.5 },
} as const

/**
 * Anti-farm caps. Care actions are pleasant but must never become the optimal
 * way to grind. Rewards taper past these daily budgets.
 *
 * These are deliberately loose now that energy, food and cleanliness are the
 * real limiters on repeated actions - the budgets are a backstop against
 * runaway automation, not a wall the player runs into during normal play.
 */
export const DAILY_CAPS = {
  bondGain: 25,
  careXp: 150,
  trainingSessions: 40,
  petInteractions: 60,
  /** Later feedings/care actions in a day give increasingly reduced bond. */
  diminishingBondAfter: 6,
  diminishedBondFactor: 0.2,
} as const

// ---------------------------------------------------------------------------
// XP / levels
// ---------------------------------------------------------------------------

export const XP = {
  /** XP required to go from `level` to `level + 1`. */
  forLevel(level: number): number {
    return Math.round(100 * Math.pow(level, 1.5))
  },
  /** Everything grants a trickle of XP, but care XP is capped daily. */
  feed: 4,
  clean: 5,
  play: 7,
  /** Bond-gain is also gently rewarded. */
  petInteraction: 0,
  levelUpHpBonus: 4,
} as const

export const MAX_LEVEL = 60

// ---------------------------------------------------------------------------
// Sanity effects (deliberately light in this milestone)
// ---------------------------------------------------------------------------

export const SANITY_EFFECTS = {
  /** Below this the creature starts to look disturbed. */
  disturbedBelow: 35,
  /** Below this the player sees unsettling behaviour. */
  fracturedBelow: 15,
} as const
